/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';

export const BIOMETRIC_TOKEN_MARKER = 'biometric-token-stored';
export const BIOMETRIC_ENROLLMENT_ACCEPTED_MARKER =
  'biometric-enrollment-accepted';

const LOG_TAG = '[BiometricService]';

/**
 * Common options applied to every plugin call:
 * - disableBackup: true  → no PIN/passcode fallback on either platform
 * - requireStrongBiometrics is enforced via isAvailable(); register/load always use the
 *   strong key created during registration.
 */
const BASE_OPTIONS: FingerprintOptions = {
  disableBackup: true,
  description: 'Usa la tua impronta digitale o Face ID per accedere',
};

@Injectable({ providedIn: 'root' })
export class BiometricService {
  /** Maps known plugin error codes (negative) to human-readable labels. */
  private static errorHint(code: number): string {
    const MAP: Record<number, string> = {
      [-100]: 'BIOMETRIC_UNKNOWN_ERROR',
      [-101]: 'BIOMETRIC_UNAVAILABLE',
      [-102]: 'BIOMETRIC_AUTHENTICATION_FAILED',
      [-103]: 'BIOMETRIC_SDK_NOT_SUPPORTED',
      [-104]: 'BIOMETRIC_HARDWARE_NOT_SUPPORTED',
      [-105]: 'BIOMETRIC_PERMISSION_NOT_GRANTED',
      [-106]: 'BIOMETRIC_NOT_ENROLLED',
      [-107]: 'BIOMETRIC_INTERNAL_PLUGIN_ERROR',
      [-108]: 'BIOMETRIC_DISMISSED',
      [-109]: 'BIOMETRIC_PIN_OR_PATTERN_DISMISSED',
      [-110]: 'BIOMETRIC_SCREEN_GUARD_UNSECURED',
      [-111]: 'BIOMETRIC_LOCKED_OUT',
      [-112]: 'BIOMETRIC_LOCKED_OUT_PERMANENT',
      [-113]:
        'BIOMETRIC_SECRET_NOT_FOUND — secret never registered or invalidated by new enrollment',
    };
    return MAP[code] ?? `unknown code ${code}`;
  }

  isAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return Promise.resolve(false);

    const fingerprintDefined = typeof Fingerprint !== 'undefined';
    if (!fingerprintDefined) return Promise.resolve(false);

    return new Promise((resolve) => {
      Fingerprint.isAvailable(
        () => {
          resolve(true);
        },
        () => {
          resolve(false);
        },
        { allowBackup: false, requireStrongBiometrics: true }
      );
    });
  }

  hasStoredSecret(): boolean {
    const value = localStorage.getItem(BIOMETRIC_TOKEN_MARKER);
    return value === 'true';
  }

  hasAcceptedEnrollment(): boolean {
    return (
      localStorage.getItem(BIOMETRIC_ENROLLMENT_ACCEPTED_MARKER) === 'true'
    );
  }

  setAcceptedEnrollment(): void {
    localStorage.setItem(BIOMETRIC_ENROLLMENT_ACCEPTED_MARKER, 'true');
  }

  public setDeclinedEnrollment(): void {
    localStorage.removeItem(BIOMETRIC_ENROLLMENT_ACCEPTED_MARKER);
  }

  registerSecret(secret: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Fingerprint.registerBiometricSecret(
        {
          ...BASE_OPTIONS,
          secret,
          invalidateOnEnrollment: true,
        },
        () => {
          localStorage.setItem(BIOMETRIC_TOKEN_MARKER, 'true');
          resolve();
        },
        (err) => {
          console.error(
            `${LOG_TAG} registerSecret() FAILED — code: ${
              err?.code
            } (${BiometricService.errorHint(err?.code)}), message: "${
              err?.message
            }"`
          );
          reject(Object.assign(new Error(err?.message), { code: err?.code }));
        }
      );
    });
  }

  loadSecret(): Promise<string> {
    return new Promise((resolve, reject) => {
      Fingerprint.loadBiometricSecret(
        { ...BASE_OPTIONS },
        (secret) => {
          localStorage.setItem(BIOMETRIC_TOKEN_MARKER, 'true');
          resolve(secret);
        },
        (err) => {
          const hint = BiometricService.errorHint(err?.code);
          console.error(
            `${LOG_TAG} loadSecret() FAILED — code: ${err?.code} (${hint}), message: "${err?.message}"`
          );
          reject(Object.assign(new Error(err?.message), { code: err?.code }));
        }
      );
    });
  }

  /** Removes the biometric marker so future app starts skip the biometric prompt. */
  clearStoredSecretMarker(): void {
    localStorage.removeItem(BIOMETRIC_TOKEN_MARKER);
  }
}
